<template>
    <div class="mx-auto">
        <page-title
            :title="$t('help.title')"
            :showTitle="false"
            :icon="'fas fa-info'"
        ></page-title>
        <div class="row justify-content-between">
            <div class="col-xl-3 col-md-6 col-sm-12">
                <div v-if="hasCreateRights">
                    <div class="btn-group" role="group">
                        <button
                            type="button"
                            class="btn btn-primary"
                            @click.stop="showCreateModal"
                        >
                            <i class="fas fa-save mr-2"></i>
                            {{ $t("buttons.create.btnText") }}
                        </button>
                    </div>
                </div>
                <div v-if="items">
                    <TreeMenu
                        v-for="item in items"
                        :key="item.id"
                        :children="item.children"
                        :depth="0"
                        :deactivated="item.deactivated"
                        :name="item.name"
                        :id="item.id"
                        :editMode="editMode"
                        @update-active="updateActive"
                        @show-edit-modal="showEditModal"
                    >
                    </TreeMenu>
                </div>
                <div v-if="editMode">
                    <hr>
                    <div class="row justify-content-between">
                        <div class="col-auto mt-0">
                            <div class="form-check">
                                <input
                                    class="form-check-input"
                                    type="checkbox"
                                    id="includeDeactivated"
                                    v-model="includeDeactivated"
                                />
                                <label
                                    class="form-check-label"
                                    for="includeDeactivated"
                                >
                                    {{$t('help.includeDeactivated')}}
                                </label>
                            </div>
                        </div>
                        <div class="col-auto mt-0 pt-0">
                            <button type="button" class="btn btn-primary" @click.stop="getMenuItems">
                                <i class="fas fa-redo mr-2"></i>
                                {{ $t("buttons.load.btnText") }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-xl-9 col-md-6 col-sm-12 pr-2">
                <div v-if="hasEditRights && !isNaN(Number(selected)) && Number(selected) > 0">
                    <div class="btn-group" role="group">
                        <button
                            v-if="!contentEditing"
                            type="button"
                            class="btn btn-success"
                            @click.stop="editContent"
                        >
                            <i class="far fa-edit">
                                {{ $t("buttons.edit.btnText") }}</i
                            >
                        </button>
                        <button
                            v-if="contentEditing"
                            type="button"
                            class="btn btn-danger"
                            @click.stop="cancelEdit"
                            :disabled="saving"
                        >
                            <i class="fas fa-ban mr-2"></i>
                            {{ $t("buttons.cancel") }}
                        </button>
                        <button
                            v-if="contentEditing"
                            type="button"
                            class="btn btn-primary"
                            @click.stop="saveContent"
                            :disabled="saving"
                        >
                            <i
                                v-if="saving"
                                class="fas fa-spinner fa-spin mr-2"
                            ></i>
                            <i v-else class="fas fa-save mr-2"></i>
                            {{ $t("buttons.save.btnText") }}
                        </button>
                    </div>
                </div>

                <div v-if="model">
                    <div v-if="hasEditRights && contentEditing">
                        <kendo-editor
                            v-model="model.content"
                            :resizable-content="true"
                            :resizable-toolbar="false"
                            :id="'helpData' + _uid"
                            :name="'helpData' + _uid"
                            rows="30"
                            style="min-height: 800px"
                        >
                        </kendo-editor>
                    </div>

                    <div v-if="!contentEditing" v-html="model.content" class="text-content"></div>
                </div>
            </div>
        </div>

        <CreateModal
            v-if="hasEditRights"
            :show="showCreate"
            @onCreate="onCreate"
            @onClose="onCreateClose"
        ></CreateModal>
        <EditModal
            v-if="hasEditRights"
            :show="showEdit"
            v-model="model"
            @onClose="onEditClose"
            @onEdit="onEdit"
        ></EditModal>
    </div>
</template>

<script>
import Vue from "vue";
import { EditorInstaller } from "@progress/kendo-editor-vue-wrapper";
import PageTitle from "@/components/common/PageTitle";
import service from "@/services/help.service";
import { UserHelp } from "@/models/models";
import { mapGetters } from "vuex";
import CreateModal from "@/components/help/create.modal";
import EditModal from "@/components/help/edit.modal";
import TreeMenu from "@/components/help/TreeMenu";

Vue.use(EditorInstaller);

export default {
    components: {
        PageTitle,
        CreateModal,
        EditModal,
        TreeMenu
    },
    props: {
        editMode: {
            type: Boolean,
            defaultt: false
        }
    },
    data() {
        return {
            active: [],
            items: [],
            model: null,
            saving: false,
            showCreate: false,
            showEdit: false,
            includeDeactivated: false,
            contentEditing: false,
            oldContent: '',
        };
    },
    methods: {
        getMenuItems() {
            service
                .getMenuItems(this.includeDeactivated)
                .then((response) => {
                    if (response.data) {
                        this.items = response.data;
                    }
                })
                .catch((error) => {
                    this.$vs.notify({
                        title: this.$t("buttons.load.btnText"),
                        text: this.$helpers.errorText(error),
                        color: "danger",
                        time: this.$store.getters.notificationDuration,
                    });
                });
        },
        loadData(id) {
            return service.getMenuItemData(id);
        },
        saveContent() {
            this.saving = true;

            service
                .saveUserHelpContent(this.model)
                .then((response) => {
                    this.$vs.notify({
                        title: this.$t("buttons.save.btnText"),
                        text: this.$helpers.responseMessage(response),
                        color: "success",
                        time: this.$store.getters.notificationDuration,
                    });

                    this.contentEditing = false;
                })
                .catch((error) => {
                    this.$vs.notify({
                        title: this.$t("buttons.save.btnText"),
                        text: this.$helpers.errorText(error),
                        color: "danger",
                        time: this.$store.getters.notificationDuration,
                    });
                })
                .then(() => {
                    this.saving = false;
                });
        },
        showCreateModal() {
            this.showCreate = true;
        },
        showEditModal(itemId) {
            if (!this.hasEditRights) {
                return this.$router.push(`/unauthorized`);
            }
            this.loadData(itemId)
                .then((response) => {
                    if (response.data) {
                        this.model = new UserHelp(response.data);
                        this.showEdit = true;
                    }
                })
                .catch((error) => {
                    this.$vs.notify({
                        title: this.$t("buttons.load.btnText"),
                        text: this.$helpers.errorText(error),
                        color: "danger",
                        time: this.$store.getters.notificationDuration,
                    });
                });
        },
        onCreateClose() {
            this.showCreate = false;
        },
        onEditClose() {
            this.showEdit = false;
        },
        onCreate(model) {
            this.getMenuItems();
        },
        onEdit(model) {
            this.getMenuItems();
        },
        updateActive(active) {
            this.active = active;
            if (this.contentEditing) {
                this.contentEditing = false;
            }
        },
        cancelEdit() {
            this.model.content = this.oldContent;
            this.oldContent = '';
            this.contentEditing = false;
        },
        editContent() {
            this.oldContent = this.model.content;
            this.contentEditing = true;
        }
    },
    mounted() {
        this.getMenuItems();
    },
    computed: {
        ...mapGetters(["claims"]),
        hasCreateRights() {
            return this.editMode && this.claims.help.create;
        },
        hasEditRights() {
            return this.editMode && this.claims.help.update;
        },
        selected() {
            return this.active;
        },
    },
    watch: {
        selected: function (val) {
            this.model = null;
            this.loadData(val)
                .then((response) => {
                    if (response.data) {
                        this.model = new UserHelp(response.data);
                    }
                })
                .catch((error) => {
                    this.$vs.notify({
                        title: this.$t("buttons.load.btnText"),
                        text: this.$helpers.errorText(error),
                        color: "danger",
                        time: this.$store.getters.notificationDuration,
                    });
                });
        },
    },
};
</script>

<style scoped lang="css">
    .text-content {
        margin-left: 21px;
    }
</style>
